import {
	FxIdDomainStoreEnumsSupportedWebPublishingPlatform,
	FxIdWebFeaturesGamesChooseGamePortalConfig,
	FxIdWebFeaturesGamesChooseGameResponsePublicGameData
} from "@app/shared/api/gen";
import { UseOSReturnValue } from "@mantine/hooks";
import { IframeType, IframeTypeToSupportedPlatformMap } from "@sdk/constants.ts";
import { FxIdSdk } from "@sdk/FxIdSdk.ts";
import { IFxIdSdkAdapterSocialSettings } from "@sdk/FxIdSdkTypes.ts";
import log from "loglevel";
import { useEffect } from "react";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

/**
 * @description Конфигурационный объект для инициализации FxIdSdk.
 * Его структура в точности соответствует параметрам конструктора SDK.
 */
export interface FxIdSdkConfig {
	game: string;
	adapter: FxIdDomainStoreEnumsSupportedWebPublishingPlatform;
	portalConfig: FxIdWebFeaturesGamesChooseGamePortalConfig;
	gameConfig: FxIdWebFeaturesGamesChooseGameResponsePublicGameData;
	os: UseOSReturnValue;
	isMobile: boolean;
}

// --- Интерфейс состояния Zustand ---

export interface IWebPlayerState {
	// --- Состояние FxId SDK ---
	/** Экземпляр инициализированного SDK. */
	fxidInstance: FxIdSdk | null;
	/** Полученные социальные настройки от SDK. */
	fxidSettings: IFxIdSdkAdapterSocialSettings | null;
	/** Флаг, указывающий на то, что идет процесс инициализации SDK. */
	isFxidLoading: boolean;
	/** Флаг, указывающий на то, что SDK успешно инициализирован. */
	isFxidInitialized: boolean;
	/** Объект ошибки, если инициализация SDK не удалась. */
	fxidError: Error | null;
	/** Асинхронный action для запуска процесса инициализации SDK. */
	initializeFxid: (config: FxIdSdkConfig) => Promise<void>;
}

// Определяем адаптер по умолчанию из переменных окружения или ставим 'fxid'.
const appDefaultAdapter: IframeType = import.meta.env.VITE_APP_DEFAULT_ADAPTER ?? "fxid";
const defaultPlatform = IframeTypeToSupportedPlatformMap[appDefaultAdapter];

/**
 * Создает хранилище состояния для веб-плеера.
 * Использует middleware 'immer' для упрощения работы с состоянием.
 */
export const useWebPlayerStore = create<IWebPlayerState>()(
	immer<IWebPlayerState>((set, get) => ({
		// --- Начальное состояние ---
		adapter: appDefaultAdapter,
		platform: defaultPlatform,
		game: "",

		fxidInstance: null,
		fxidSettings: null,
		isFxidLoading: false,
		isFxidInitialized: false,
		fxidError: null,

		// --- Действия ---

		/**
		 * Инициализирует FxId SDK.
		 * Эта функция является идемпотентной: она выполнит логику инициализации только один раз.
		 */
		initializeFxid: async (config) => {
			if (get().isFxidLoading || get().isFxidInitialized) {
				log.debug("FxIdSdk initialization is already in progress or completed");
				return;
			}

			set((state) => {
				state.isFxidLoading = true;
				state.fxidError = null;
			});

			log.debug("Starting FxIdSdk initialization");

			try {
				// Деструктурируем конфиг для передачи в конструктор.
				// Имена полей в config должны совпадать с параметрами конструктора.
				const { game, adapter, portalConfig, gameConfig, os, isMobile } = config;

				// Вызов конструктора с точными, типизированными параметрами.
				const sdkInstance = new FxIdSdk(game, adapter, portalConfig, gameConfig, os, isMobile);
				await sdkInstance.InitializeSocialAdapter();
				await sdkInstance.LoginToFxId();

				log.debug("FxIdSdk login successful, fetching social settings");
				const settings = await sdkInstance.SocialSettings();

				log.debug("FxIdSdk initialization and settings fetch successful");

				window.FxIdSdk = sdkInstance;

				set((state) => {
					state.fxidInstance = sdkInstance;
					state.fxidSettings = settings;
					state.isFxidInitialized = true;
					state.isFxidLoading = false;
				});
			} catch (err) {
				const error =
					err instanceof Error ? err : new Error("An unknown error occurred during FxIdSdk initialization");
				log.error("Failed to initialize FxIdSdk", error);
				set((state) => {
					state.fxidError = error;
					state.isFxidLoading = false;
					state.isFxidInitialized = false; // Позволяет повторить попытку
				});
			}
		}
	}))
);
export const useWebPlayerUiStore = useWebPlayerStore;

/**
 * Хук-эффект для запуска инициализации FxId SDK при монтировании компонента.
 * Этот хук следует использовать **один раз** в корневом компоненте вашего приложения (например, App.tsx).
 */
export const useInitializeFxId = (config: FxIdSdkConfig | null | undefined) => {
	if (config == null) {
		log.info("useInitializeFxId - no config yet");
		return null;
	}

	const initialize = useWebPlayerStore((state) => state.initializeFxid);

	useEffect(() => {
		void initialize(config);
	}, [initialize]);
};
